import type { TFunction } from "i18next";
import {
  Bell,
  CalendarDays,
  CalendarPlus,
  CalendarRange,
  ChartLine,
  Clock,
  FilePlus,
  FileText,
  Megaphone,
  Package,
  PackagePlus,
  Receipt,
  ReceiptText,
  SquareKanban,
  Timer,
  Users,
  type LucideIcon,
} from "lucide-react";
import type {
  ChatShortcutAction,
  ChatShortcutEntityType,
} from "@/types/(my-space)/chat.interface";
import type { FileTone } from "./file-badge";

/**
 * In-app destinations a "Shortcut" message can point at. `route` is the API's
 * DeepLinkRoute value (validated server-side, shared with the mobile app) —
 * data, never translated; `path` is where it lands in this portal.
 */
export interface IChatShortcut {
  route: string;
  path: string;
  icon: LucideIcon;
  label: (t: TFunction) => string;
}

export const CHAT_SHORTCUTS: IChatShortcut[] = [
  {
    route: "leave",
    path: "/leave-management",
    icon: CalendarRange,
    label: (t) => t("chat:shortcut.leave"),
  },
  {
    route: "time-attendance",
    path: "/time-attendance",
    icon: Timer,
    label: (t) => t("chat:shortcut.attendance"),
  },
  {
    route: "announcements",
    path: "/announcements",
    icon: Megaphone,
    label: (t) => t("chat:shortcut.announcements"),
  },
  {
    route: "documents",
    path: "/documents",
    icon: FileText,
    label: (t) => t("chat:shortcut.documents"),
  },
  {
    route: "events",
    path: "/events",
    icon: CalendarDays,
    label: (t) => t("chat:shortcut.events"),
  },
  {
    route: "team",
    path: "/teams",
    icon: Users,
    label: (t) => t("chat:shortcut.teams"),
  },
  {
    route: "asset-requests",
    path: "/assets",
    icon: Package,
    label: (t) => t("chat:shortcut.assets"),
  },
  {
    route: "performance-evaluation",
    path: "/performance",
    icon: ChartLine,
    label: (t) => t("chat:shortcut.performance"),
  },
  {
    route: "notifications",
    path: "/notifications",
    icon: Bell,
    label: (t) => t("chat:shortcut.notifications"),
  },
];

export const chatShortcutFor = (route: string | undefined) =>
  CHAT_SHORTCUTS.find((s) => s.route === route);

// ── Specific items ("My items") ─────────────────────────────────────────────

export interface IChatShortcutKind {
  type: ChatShortcutEntityType;
  icon: LucideIcon;
  tone: FileTone;
  /** The kind as a list heading ("Leave requests"). */
  label: (t: TFunction) => string;
}

export const CHAT_SHORTCUT_KINDS: IChatShortcutKind[] = [
  {
    type: "leave_request",
    icon: CalendarRange,
    tone: "info",
    label: (t) => t("chat:shortcut.kinds.leave_request"),
  },
  {
    type: "document_request",
    icon: FileText,
    tone: "danger",
    label: (t) => t("chat:shortcut.kinds.document_request"),
  },
  {
    type: "expense",
    icon: Receipt,
    tone: "success",
    label: (t) => t("chat:shortcut.kinds.expense"),
  },
  {
    type: "asset_request",
    icon: Package,
    tone: "warning",
    label: (t) => t("chat:shortcut.kinds.asset_request"),
  },
  {
    type: "event",
    icon: CalendarDays,
    tone: "accent",
    label: (t) => t("chat:shortcut.kinds.event"),
  },
  {
    type: "announcement",
    icon: Megaphone,
    tone: "primary",
    label: (t) => t("chat:shortcut.kinds.announcement"),
  },
  {
    type: "task",
    icon: SquareKanban,
    tone: "info",
    label: (t) => t("chat:shortcut.kinds.task"),
  },
];

export const chatShortcutKindFor = (type: string | undefined) =>
  CHAT_SHORTCUT_KINDS.find((k) => k.type === type);

// ── Actions (open a form) ───────────────────────────────────────────────────

export interface IChatShortcutActionDef {
  action: ChatShortcutAction;
  icon: LucideIcon;
  label: (t: TFunction) => string;
}

export const CHAT_SHORTCUT_ACTION_DEFS: IChatShortcutActionDef[] = [
  {
    action: "apply_leave",
    icon: CalendarPlus,
    label: (t) => t("chat:shortcut.actions.apply_leave"),
  },
  {
    action: "request_document",
    icon: FilePlus,
    label: (t) => t("chat:shortcut.actions.request_document"),
  },
  {
    action: "submit_expense",
    icon: ReceiptText,
    label: (t) => t("chat:shortcut.actions.submit_expense"),
  },
  {
    action: "request_asset",
    icon: PackagePlus,
    label: (t) => t("chat:shortcut.actions.request_asset"),
  },
  {
    action: "clock_in",
    icon: Clock,
    label: (t) => t("chat:shortcut.actions.clock_in"),
  },
];

export const chatShortcutActionFor = (action: string | undefined) =>
  CHAT_SHORTCUT_ACTION_DEFS.find((a) => a.action === action);
